package com.dotdrives.web.pages.donations

import com.dotdrives.controller.RecurringSchedulesController
import com.dotdrives.controller.RecurringManagementController
import com.dotdrives.repository.SupabaseDonorRepository
import com.dotdrives.web.BrowserDonorOperations
import com.dotdrives.model.RecurringSchedule
import com.dotdrives.repository.SupabaseRecurringScheduleRepository
import com.dotdrives.web.AppRoute
import com.dotdrives.web.entityRouteHash
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotSkeleton
import com.dotdrives.web.ui.useFlowState
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.cancel
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.style
import react.useEffect
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text

external interface RecurringSchedulesPanelProps : Props {
    var userId: String
    var orgId: String
    var giverId: String?
}

/** Mount only when selected; the key isolates all requests and state by organization and giver. */
val RecurringSchedulesPanel = FC<RecurringSchedulesPanelProps> { props ->
    RecurringSchedulesContent {
        key = "${props.userId}:${props.orgId}:${props.giverId.orEmpty()}".unsafeCast<Key>()
        userId = props.userId
        orgId = props.orgId
        giverId = props.giverId
    }
}

private val RecurringSchedulesContent = FC<RecurringSchedulesPanelProps> { props ->
    val (scope) = useState { MainScope() }
    val (controller) = useState { RecurringSchedulesController(SupabaseRecurringScheduleRepository(), scope, props.orgId, props.giverId) }
    val (management) = useState {
        RecurringManagementController(props.orgId, scope, SupabaseDonorRepository()::recurring,
            BrowserDonorOperations(props.userId, "staff-recurring"))
    }
    val state = useFlowState(controller.state)
    useEffect(controller) {
        management.start()
        controller.load()
        try { awaitCancellation() } finally { management.close(); controller.close(); scope.cancel() }
    }
    style { +recurringStyles }
    div {
        className = ClassName("recurring-panel" + if (props.giverId != null) " recurring-panel-compact" else "")
        RecurringManagementNotice { this.controller = management }
        div { className = ClassName("recurring-intro"); +"Recurring donation schedules" }
        div { className = ClassName("recurring-description"); +"View ongoing gifts and their next scheduled payment. Status and dates reflect the latest saved verification. Reload retrieves saved data; it does not contact Stripe. Imported schedules awaiting verification are listed under Needs review." }
        div {
            className = ClassName("recurring-toolbar")
            select {
                className = ClassName("recurring-select")
                ariaLabel = "Recurring schedule status"
                value = state.status
                onChange = { controller.filter(it.currentTarget.value) }
                listOf("active" to "Active", "needs_review" to "Needs review", "ended" to "Ended", "all" to "All schedules").forEach { (value, label) ->
                    option { this.value = value; +label }
                }
            }
            input {
                className = ClassName("recurring-search")
                type = InputType.text
                ariaLabel = "Search recurring schedules"
                placeholder = if (props.giverId == null) "Search giver or target…" else "Search target…"
                value = state.query
                onChange = { controller.search(it.currentTarget.value) }
            }
            button {
                type = ButtonType.buttonType
                className = ClassName("recurring-button")
                disabled = state.loading
                onClick = { controller.load() }
                +"Reload"
            }
        }
        if (!state.loading && state.error == null) div {
            className = ClassName("recurring-result-count")
            +"${state.total} schedule${if (state.total == 1) "" else "s"}"
        }
        when {
            state.loading -> div { repeat(3) { DotSkeleton { height = "94px" } } }
            state.error != null -> DotBanner { message = state.error!!; onRetry = controller::load }
            state.items.isEmpty() -> div {
                className = ClassName("recurring-empty")
                +when {
                    state.query.isNotBlank() -> "No schedules match your search."
                    state.status == "active" -> "No verified active schedules. Check Needs review for imported schedules awaiting verification."
                    state.status == "needs_review" -> "No schedules need review."
                    state.status == "ended" -> "No ended schedules."
                    else -> "No recurring schedules yet."
                }
            }
            else -> state.items.forEach { item ->
                div {
                    key = item.id.unsafeCast<Key>()
                    className = ClassName("recurring-row")
                    div {
                        className = ClassName("recurring-main")
                        if (props.giverId == null) a {
                            className = ClassName("recurring-giver")
                            href = entityRouteHash(AppRoute.Givers, props.orgId, item.giverId)
                            +item.giverName
                        }
                        div { className = ClassName("recurring-amount"); +formatMoneyFull(item.amount); span { +" ${item.currency.uppercase()} · ${recurringCadence(item.interval)}" } }
                        div { className = ClassName("recurring-target"); +recurringTargetLabel(item) }
                    }
                    div {
                        className = ClassName("recurring-next")
                        span { className = ClassName("recurring-label"); +"NEXT PAYMENT" }
                        div { +recurringNextPayment(item) }
                        if (recurringEndsAfterCurrentPeriod(item)) div { className = ClassName("recurring-note"); +"Ends after current period" }
                    }
                    div {
                        className = ClassName("recurring-state")
                        span { className = ClassName("recurring-status" + if (item.status == "active" && item.verification != "legacy_unverified") " recurring-status-active" else ""); +recurringStatus(item) }
                        div {
                            className = ClassName("recurring-note")
                            +when (item.verification) {
                                "provider_verified" -> recurringVerificationLabel(item)
                                "legacy_unverified" -> "Imported · Needs verification"
                                else -> "Tracked in Dot Drives"
                            }
                        }
                    }
                    RecurringScheduleControls { this.item = item; this.controller = management }
                }
            }
        }
        if (!state.loading && state.error == null && state.total > 0) div {
            className = ClassName("recurring-pagination")
            span { +"${state.offset + 1}–${state.offset + state.items.size} of ${state.total} schedules" }
            div {
                button { type = ButtonType.buttonType; className = ClassName("recurring-button"); disabled = !state.hasPrevious; onClick = { controller.previous() }; +"Previous" }
                button { type = ButtonType.buttonType; className = ClassName("recurring-button"); disabled = !state.hasNext; onClick = { controller.next() }; +"Next" }
            }
        }
    }
}

internal fun recurringTargetLabel(item: RecurringSchedule): String = item.targetName ?: "Target not recorded"

internal fun recurringEndsAfterCurrentPeriod(item: RecurringSchedule): Boolean =
    item.cancelAtPeriodEnd && item.status !in setOf("canceled", "paused", "incomplete_expired")

internal fun recurringVerificationLabel(item: RecurringSchedule): String =
    "Last verified with Stripe" + (item.verifiedAt?.let { " · ${dateChip(it)} ${it.toString().take(4)}" } ?: "")

internal fun recurringCadence(interval: String): String = when (interval) {
    "monthly", "month" -> "Monthly"
    "quarterly" -> "Quarterly"
    "yearly", "annual", "year" -> "Yearly"
    else -> interval.replace('_', ' ').replaceFirstChar { it.uppercase() }
}

internal fun recurringStatus(item: RecurringSchedule): String = when {
    item.verification == "legacy_unverified" -> "Needs verification"
    item.status == "past_due" -> "Past due"
    item.status == "trialing" -> "Trial"
    item.status == "incomplete_expired" -> "Expired"
    else -> item.status.replace('_', ' ').replaceFirstChar { it.uppercase() }
}

internal fun recurringNextPayment(item: RecurringSchedule): String = when {
    item.verification == "legacy_unverified" -> "Awaiting verification"
    item.cancelAtPeriodEnd || item.status in setOf("canceled", "paused", "incomplete_expired") -> "Not scheduled"
    else -> item.nextChargeAt?.let { "${dateChip(it)} ${it.toString().take(4)}" } ?: "Not available"
}

private val recurringStyles = """
.recurring-panel {display:flex;flex-direction:column;gap:12px;min-width:0}
.recurring-intro {font-size:17px;font-weight:800;color:#222D32}
.recurring-description {font-size:13px;color:#60737E;line-height:1.55;max-width:740px;margin-top:-5px}
.recurring-result-count {font-size:12px;font-weight:700;color:#617681}
.recurring-toolbar {display:flex;gap:10px;flex-wrap:wrap;margin:4px 0}
.recurring-select,.recurring-search,.recurring-button {border:1px solid #D4DEE4;border-radius:10px;background:white;min-height:40px;padding:9px 12px;font:inherit;font-size:13px;color:#33454F}
.recurring-search {flex:1;min-width:150px}
.recurring-button {cursor:pointer;font-weight:700}
.recurring-button:disabled {opacity:.45;cursor:default}
.recurring-button:focus-visible,.recurring-select:focus-visible,.recurring-search:focus-visible,.recurring-giver:focus-visible {outline:2px solid #B84818;outline-offset:3px}
.recurring-row {display:grid;grid-template-columns:minmax(200px,1.3fr) minmax(140px,.8fr) minmax(160px,.9fr);gap:20px;padding:18px 20px;background:white;border:1px solid #E0E7EB;border-radius:12px;align-items:center}
.recurring-giver {display:inline-block;color:#25343D;font-weight:800;font-size:14px;text-decoration:underline;text-decoration-color:#CBD6DD;text-underline-offset:3px;margin-bottom:7px;overflow-wrap:anywhere}
.recurring-giver:hover {color:#B84818}
.recurring-amount {font-weight:800;font-size:16px;color:#25343D}
.recurring-amount span {font-size:12px;font-weight:500;color:#617681}
.recurring-target {font-size:12px;color:#617681;margin-top:5px;overflow-wrap:anywhere}
.recurring-next {font-size:13px;color:#33454F;line-height:1.8}
.recurring-label {display:block;font-size:10px;letter-spacing:.7px;color:#617681;font-weight:700}
.recurring-status {display:inline-block;padding:5px 9px;border-radius:20px;background:#F0F3F5;color:#536974;font-size:11px;font-weight:800}
.recurring-status-active {background:#E8F3EC;color:#2E6444}
.recurring-note {font-size:11px;color:#667984;line-height:1.5;margin-top:5px}
.recurring-empty {padding:36px 24px;border:1px dashed #CBD7DE;border-radius:12px;background:white;color:#617681;font-size:14px;line-height:1.6;text-align:center}
.recurring-pagination {display:flex;gap:12px;justify-content:space-between;align-items:center;font-size:12px;color:#617681;flex-wrap:wrap}
.recurring-pagination>div {display:flex;gap:8px}
.recurring-panel-compact .recurring-row {grid-template-columns:1fr 1fr;gap:12px;padding:16px}
.recurring-panel-compact .recurring-state {grid-column:1/-1;display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.recurring-panel-compact .recurring-description {font-size:12px}
@media(max-width:760px){.recurring-row {grid-template-columns:1fr 1fr;gap:12px;padding:16px}.recurring-state {grid-column:1/-1;display:flex;gap:8px;align-items:center;flex-wrap:wrap}.recurring-search {min-width:140px}.recurring-toolbar {gap:7px}}
""".trimIndent()
